// [2026-09-02] ADDED — the money screen: what every tenant was granted, spent and still owes us.
//
// TWO QUESTIONS, ONE SCREEN. The table answers "who do we invoice and for how much"; the ledger
// below answers "what moved, when, and who moved it". They are on the same page because an
// unexpected number in the first is always chased into the second.
//
// `to_invoice` IS THE PLATFORM TOTAL OF SPEND, not of balances — a balance is money we already hold.
// Naming them apart matters: conflating the two is how a platform bills for credits it was paid for
// in advance.

import { useState } from 'react'
import {
  Card, Empty, Notice, Skeleton, Tile, num, when, downloadCsv, useAsync,
} from '@incruiter/proctoring-client-frontend'
import { Badge } from '@incruiter/proctoring-client-frontend/src/components/ui/badge.jsx'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@incruiter/proctoring-client-frontend/src/components/ui/table.jsx'
import { admin } from '../api.js'

const TYPE_VARIANT = { grant: 'healthy', debit: 'serious', settle: 'serious',
                       adjust: 'degraded', reserve: 'idle', release: 'idle' }

export default function Billing({ days = 30, onError, onOpenClient }) {
  const [search, setSearch] = useState('')
  const { data, error, loading } = useAsync(() => admin.billing(days), [days])
  const { data: ledger } = useAsync(() => admin.ledger(100), [])

  if (error) { onError?.(error); return <Notice kind="error">{error.message}</Notice> }
  if (loading || !data) return <Skeleton height={280} />

  const totals = data.totals || {}
  const term = search.trim().toLowerCase()
  const rows = (data.clients || []).filter((c) => !term
    || [c.client_id, c.name, c.contact_email].filter(Boolean).some((v) => String(v).toLowerCase().includes(term)))

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gap: 12,
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
        <Tile label="Granted" value={num(totals.granted || 0, { decimals: 0 })} unit="credits" />
        <Tile label="Spent" value={num(totals.spent || 0, { decimals: 2 })} unit="credits" />
        <Tile label="Outstanding" value={num(totals.outstanding || 0, { decimals: 2 })} unit="credits"
              sub="balances we still hold" />
        <Tile label="Held" value={num(totals.held || 0, { decimals: 2 })} unit="credits"
              sub="reserved by in-flight runs" />
        <Tile label="To invoice" value={num(totals.to_invoice || 0, { decimals: 2 })} unit="credits" />
      </div>

      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', padding: '18px 20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12,
                      flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontWeight: 600 }}>Billing by tenant</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              {rows.length} tenants · click a row to open it
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search"
                   placeholder="Search email or company…" style={{ minWidth: 200 }} />
            <button className="btn ghost" disabled={!rows.length}
                    onClick={() => downloadCsv('billing.csv', rows)}>Export CSV</button>
          </div>
        </div>

        {rows.length === 0 ? <Empty title="No tenants" hint="" /> : (
          <div style={{ overflowX: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Granted</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Spent</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Adjusted</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Held</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Balance</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Entries</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.client_id} style={{ cursor: 'pointer' }}
                            onClick={() => onOpenClient?.(c.client_id)}>
                    {/* [2026-09-03] Email shown here for the same reason as the tenants table: a
                        generated client_id identifies nobody, and this is the screen where money is
                        reconciled against a real company. */}
                    <TableCell>
                      <div>{c.name || c.client_id}</div>
                      {c.contact_email && (
                        <div className="muted" style={{ fontSize: 12 }}>{c.contact_email}</div>
                      )}
                      <div className="muted mono" style={{ fontSize: 11 }}>{c.client_id}</div>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {num(c.credits_granted || 0, { decimals: 0 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {num(c.credits_spent || 0, { decimals: 2 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {num(c.credits_adjusted || 0, { decimals: 2 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      {num(c.credits_held || 0, { decimals: 2 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right',
                                        color: c.low_balance ? 'var(--critical)' : undefined }}>
                      {num(c.credits_balance || 0, { decimals: 2 })}
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }} className="muted">
                      {num(c.ledger_entries || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <Card title="Recent credit movements" subtitle="Across every tenant, newest first">
        {!(ledger?.entries || []).length ? <Empty title="No movements yet" hint="" /> : (
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead><TableHead>Tenant</TableHead><TableHead>Type</TableHead>
                  <TableHead style={{ textAlign: 'right' }}>Change</TableHead>
                  <TableHead>Reason</TableHead><TableHead>Actor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ledger.entries.map((e) => {
                  const delta = (e.amount_milli || 0) / 1000
                  return (
                    <TableRow key={e.entry_id}>
                      <TableCell className="muted">{when(e.created_at)}</TableCell>
                      <TableCell className="mono" style={{ fontSize: 12 }}>{e.client_id}</TableCell>
                      <TableCell><Badge variant={TYPE_VARIANT[e.type] || 'idle'}>{e.type}</Badge></TableCell>
                      <TableCell style={{ textAlign: 'right',
                                          color: delta >= 0 ? 'var(--good)' : 'var(--critical)' }}>
                        {delta >= 0 ? '+' : ''}{num(delta, { decimals: 3 })}
                      </TableCell>
                      <TableCell className="muted">{e.reason || '—'}</TableCell>
                      <TableCell className="muted">{e.actor || '—'}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  )
}
